import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import casinoWS from '../services/websocket';

// Split into 3 sub-contexts by update frequency:
// - Toggle: chatOpen state (changes on user click only)
// - Messages: chatMessages + rateLimitError (changes on every chat message)
// - Meta: onlineCount + tutorialCompleted (changes infrequently)
const ChatToggleContext = createContext(null);
const ChatMessagesContext = createContext(null);
const ChatMetaContext = createContext(null);

export function ChatProvider({ children }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [chatRateLimitError, setChatRateLimitError] = useState(null);
  const [tutorialCompleted, setTutorialCompleted] = useState(true);
  const errorTimerRef = useRef(null);

  useEffect(() => {
    const unsubscribers = [];

    unsubscribers.push(casinoWS.on('init', (data) => {
      setTutorialCompleted(data.tutorialCompleted === true);
    }));

    unsubscribers.push(casinoWS.on('reconnecting', () => {
      setChatMessages([]);
    }));

    unsubscribers.push(casinoWS.on('chatHistory', (data) => {
      if (!data.messages || !Array.isArray(data.messages)) return;

      const formattedMessages = data.messages.map(msg => {
        return {
          id: msg.id || `${msg.steamId}-${msg.timestamp}`,
          steamId: msg.steamId,
          username: msg.name || 'Anonymous',
          avatarUrl: msg.avatarUrl || null,
          text: msg.text,
          timestamp: msg.timestamp,
          cosmetics: msg.cosmetics || null,
          ownedEmotes: msg.ownedEmotes || [],
        };
      });

      setChatMessages(formattedMessages.slice(-100));
    }));

    unsubscribers.push(casinoWS.on('chat', (data) => {
      setChatMessages(prev => {
        const msgId = data.id || `${data.steamId}-${data.timestamp || Date.now()}`;
        if (prev.some(msg => msg.id === msgId)) return prev;
        const newMsg = {
          id: msgId,
          steamId: data.steamId,
          username: data.name || data.username || 'Anonymous',
          avatarUrl: data.avatarUrl || null,
          text: data.text,
          timestamp: data.timestamp,
          cosmetics: data.cosmetics || null,
          ownedEmotes: data.ownedEmotes || [],
        };
        return [...prev, newMsg].slice(-100);
      });
    }));

    unsubscribers.push(casinoWS.on('playerJoin', (data) => {
      setChatMessages(prev => {
        const exists = prev.some(msg =>
          msg.type === 'player-join' && msg.id === data.id
        );
        if (exists) return prev;
        const joinMsg = {
          id: data.id,
          type: 'player-join',
          text: data.text,
          joinMessageStyle: data.joinMessageStyle,
          joinerName: data.joinerName,
          rarity: data.rarity || 'default',
          timestamp: data.timestamp,
        };
        return [...prev, joinMsg].slice(-100);
      });
    }));

    unsubscribers.push(casinoWS.on('onlineCount', (data) => {
      setOnlineCount(data.count || 0);
    }));

    unsubscribers.push(casinoWS.on('chatError', (data) => {
      setChatRateLimitError(data.message || 'Rate limit exceeded');
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => setChatRateLimitError(null), 3000);
    }));

    unsubscribers.push(casinoWS.on('chatCleared', () => {
      setChatMessages([]);
    }));

    return () => {
      unsubscribers.forEach(unsub => unsub());
      clearTimeout(errorTimerRef.current);
    };
  }, []);

  const handleChatToggle = useCallback(() => {
    setChatOpen(prev => !prev);
  }, []);

  const handleSendChatMessage = useCallback((text) => {
    if (text && text.trim()) {
      casinoWS.sendChat(text.trim());
    }
  }, []);

  // Each sub-context memoized independently — a chatMessage change
  // does NOT cause toggleValue or metaValue consumers to re-render
  const toggleValue = useMemo(() => ({
    chatOpen,
    handleChatToggle,
  }), [chatOpen, handleChatToggle]);

  const messagesValue = useMemo(() => ({
    chatMessages,
    chatRateLimitError,
    handleSendChatMessage,
  }), [chatMessages, chatRateLimitError, handleSendChatMessage]);

  const metaValue = useMemo(() => ({
    onlineCount,
    tutorialCompleted,
  }), [onlineCount, tutorialCompleted]);

  return (
    <ChatToggleContext.Provider value={toggleValue}>
    <ChatMessagesContext.Provider value={messagesValue}>
    <ChatMetaContext.Provider value={metaValue}>
      {children}
    </ChatMetaContext.Provider>
    </ChatMessagesContext.Provider>
    </ChatToggleContext.Provider>
  );
}

// Granular hooks — consumers subscribe only to the slice they need
export function useChatToggle() {
  const ctx = useContext(ChatToggleContext);
  if (!ctx) throw new Error('useChatToggle must be used within ChatProvider');
  return ctx;
}

export function useChatMessages() {
  const ctx = useContext(ChatMessagesContext);
  if (!ctx) throw new Error('useChatMessages must be used within ChatProvider');
  return ctx;
}

export function useChatMeta() {
  const ctx = useContext(ChatMetaContext);
  if (!ctx) throw new Error('useChatMeta must be used within ChatProvider');
  return ctx;
}

// Convenience hook for components that need everything (ChatPanel)
export function useChat() {
  return { ...useChatToggle(), ...useChatMessages(), ...useChatMeta() };
}
